import { useState, useEffect, useCallback } from "react";
import { categoryService } from "../services/categoryService";

export const useCategoriesAPI = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load all categories
  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await categoryService.getAll();
      setCategories(data);
    } catch (err) {
      console.error("Failed to load categories:", err);
      setError(err.message || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, []);


  // Get a single category by ID
  const getCategory = useCallback(async (id) => {
    try {
      setError(null);
      return await categoryService.getById(id);
    } catch (err) {
      console.error("Failed to get category:", err);
      setError(err.message || "Failed to get category");
      throw err;
    }
  }, []);

  // Build hierarchical category tree
  const buildCategoryTree = useCallback(
    (categoriesToProcess = categories) => {
      const categoryMap = new Map();
      const rootCategories = [];

      // Create a map of all categories
      categoriesToProcess.forEach((category) => {
        categoryMap.set(category._id, { ...category, children: [] });
      });

      // Build the tree structure
      categoriesToProcess.forEach((category) => {
        if (category.parent_id) {
          const parent = categoryMap.get(category.parent_id);
          if (parent) {
            parent.children.push(categoryMap.get(category._id));
          }
        } else {
          rootCategories.push(categoryMap.get(category._id));
        }
      });

      return rootCategories;
    },
    [categories]
  );

  // Filter categories based on search term
  const filterCategories = useCallback(
    (searchTerm = "") => {
      if (!searchTerm) return categories;

      return categories.filter(
        (category) =>
          category &&
          category.name &&
          category.slug &&
          (category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            category.slug.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    },
    [categories]
  );

  // Get category by slug
  const getCategoryBySlug = useCallback(
    (slug) => {
      return categories.find((category) => category.slug === slug);
    },
    [categories]
  );

  // Get category name by ID
  const getCategoryName = useCallback(
    (categoryId) => {
      const category = categories.find((cat) => cat._id === categoryId);
      return category ? category.name : "Uncategorized";
    },
    [categories]
  );

  // Check if category has subcategories
  const hasSubcategories = useCallback(
    (categoryId) => {
      return categories.some((cat) => cat.parent_id === categoryId);
    },
    [categories]
  );

  // Get subcategories of a parent category
  const getSubcategories = useCallback(
    (parentId) => {
      return categories.filter((category) => category.parent_id === parentId);
    },
    [categories]
  );

  // Get root categories (no parent)
  const getRootCategories = useCallback(() => {
    return categories.filter((category) => !category.parent_id);
  }, [categories]);

  // Check if category can be deleted (no subcategories or products)
  const canDeleteCategory = useCallback(
    (categoryId) => {
      // Check for subcategories
      const hasSubcats = hasSubcategories(categoryId);
      return !hasSubcats;
    },
    [hasSubcategories]
  );

  // Get deletion block message
  const getDeletionBlockMessage = useCallback(
    (categoryId) => {
      const hasSubcats = hasSubcategories(categoryId);

      if (hasSubcats) {
        return "This category contains subcategories. Please delete or move the subcategories first.";
      }

      return "This category may contain products. Please move or delete the products first.";
    },
    [hasSubcategories]
  );

  // Get category path (breadcrumb)
  const getCategoryPath = useCallback(
    (categoryId) => {
      const path = [];
      let currentCategory = categories.find((cat) => cat._id === categoryId);

      while (currentCategory) {
        path.unshift(currentCategory);
        const parentId = currentCategory.parent_id;
        currentCategory = parentId
          ? categories.find((cat) => cat._id === parentId)
          : null;
      }

      return path;
    },
    [categories]
  );

  // Initial load
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  return {
    // State
    categories,
    loading,
    error,

    // Actions
    loadCategories,
    getCategory,

    // Utility functions
    buildCategoryTree,
    filterCategories,
    getCategoryBySlug,
    getCategoryName,
    hasSubcategories,
    getSubcategories,
    getRootCategories,
    canDeleteCategory,
    getDeletionBlockMessage,
    getCategoryPath,

    // Computed values
    totalCategories: categories.length,
    rootCategoriesCount: categories.filter((cat) => !cat.parent_id).length,
    subcategoriesCount: categories.filter((cat) => cat.parent_id).length,
  };
};

export default useCategoriesAPI;
